import React, { useCallback, useMemo } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Input, InputHeader, InputWrapper } from '@amzn/stencil-react-components/input'
import { Col } from '@amzn/stencil-react-components/layout'
import { PopupDatePicker } from '@amzn/stencil-react-components/popup-date-picker'
import { Select } from '@amzn/stencil-react-components/select'
import { H3 } from '@amzn/stencil-react-components/text'

import { ModuleType } from 'src/models/dto/ModuleDTO'
import { ModuleStatus } from 'src/models/dto/ModuleStatus'

export enum FILTER_DATA_IDS {
    OWNER_INPUT = 'owner-input',
    MODIFIED_BY_INPUT = 'modified-by-input',
    START_DATE_INPUT = 'start-date-input',
    END_DATE_INPUT = 'end-date-input',
    MODULE_TYPE_SELECT = 'module-type-select',
    MODULE_STATUS_SELECT = 'module-status-select',
}

interface ModuleListFilterProps {
    title?: string
    owner: string
    moduleStatus: ModuleStatus[]
    moduleTypes: ModuleType[]
    lastModifiedBy: string
    startDate: string
    endDate: string
    lockedModuleStatus?: ModuleStatus[]
    disableFilters?: FILTER_DATA_IDS[]
    setOwner: (owner: string) => void
    setLastModifiedBy: (lastModifiedBy: string) => void
    setStartDate: (start: string) => void
    setEndDate: (end: string) => void
    setModuleTypes: (moduleTypes: ModuleType[]) => void
    setModuleStatus: (moduleStatus: ModuleStatus[]) => void
    applyFilters?: () => void
}

const ModuleListFilterInner = (props: ModuleListFilterProps) => {
    const {
        moduleTypes,
        moduleStatus: status,
        startDate,
        endDate,
        owner,
        lastModifiedBy,
        lockedModuleStatus,
        setOwner,
        setLastModifiedBy,
        setStartDate,
        setEndDate,
        setModuleTypes,
        setModuleStatus,
    } = props

    const handleOwnerChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setOwner(e.target.value)
        },
        [setOwner]
    )
    const handleLastModifiedByChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setLastModifiedBy(e.target.value)
        },
        [setLastModifiedBy]
    )
    const getSelectTriggerText = useCallback(
        (numberOfSelectedItems: number) =>
            numberOfSelectedItems === 1
                ? '1 status selected'
                : `${numberOfSelectedItems} statuses selected`,
        []
    )
    return (
        <Col gridGap='S300'>
            {props.title && <H3>Filters</H3>}

            <Col gridGap='S300' aria-label='Filters'>
                <InputHeader
                    labelText='Owner'
                    htmlFor='owner-input'
                    tooltipText='user alias of module owner'
                />
                {useMemo(
                    () => (
                        <Input
                            id='owner-input'
                            dataTestId={FILTER_DATA_IDS.OWNER_INPUT}
                            placeholder='Anyone'
                            aria-label='Module owner'
                            value={owner}
                            onChange={handleOwnerChange}
                        />
                    ),
                    [handleOwnerChange, owner]
                )}

                {useMemo(
                    () => (
                        <InputWrapper id='modified-by-input' labelText='Last modified by'>
                            {(inputProps) => (
                                <Input
                                    {...inputProps}
                                    dataTestId={FILTER_DATA_IDS.MODIFIED_BY_INPUT}
                                    placeholder='Anyone'
                                    aria-label='Modified by'
                                    value={lastModifiedBy}
                                    onChange={handleLastModifiedByChange}
                                />
                            )}
                        </InputWrapper>
                    ),
                    [handleLastModifiedByChange, lastModifiedBy]
                )}

                {useMemo(
                    () => (
                        <PopupDatePicker
                            id='start-date-input'
                            dataTestId={FILTER_DATA_IDS.START_DATE_INPUT}
                            labelText='Start date'
                            inputProps={{ width: '100%' }}
                            value={startDate}
                            onChange={setStartDate}
                        />
                    ),
                    [startDate, setStartDate]
                )}

                {useMemo(
                    () => (
                        <PopupDatePicker
                            id='end-date-input'
                            dataTestId={FILTER_DATA_IDS.END_DATE_INPUT}
                            labelText='End date'
                            inputProps={{ width: '100%' }}
                            value={endDate}
                            onChange={setEndDate}
                        />
                    ),
                    [endDate, setEndDate]
                )}

                {useMemo(
                    () => (
                        <InputWrapper id='module-type-input' labelText='Module type'>
                            {(inputProps) => (
                                <Select
                                    {...inputProps}
                                    dataTestId={FILTER_DATA_IDS.MODULE_TYPE_SELECT}
                                    options={[
                                        ModuleType.Consent,
                                        ModuleType.Content,
                                        ModuleType.Evaluation,
                                        ModuleType.FormTemplate,
                                        ModuleType.Instructional,
                                        ModuleType.ItemsContainer,
                                        ModuleType.Landing,
                                        ModuleType.Transition,
                                        ModuleType.Other,
                                    ]}
                                    isMulti={true}
                                    onChange={setModuleTypes}
                                    placeholder='Any module type'
                                    value={moduleTypes}
                                    disabled={
                                        props.disableFilters &&
                                        props.disableFilters.includes(
                                            FILTER_DATA_IDS.MODULE_TYPE_SELECT
                                        )
                                    }
                                    getTriggerText={(numberOfSelectedItems) =>
                                        numberOfSelectedItems === 1
                                            ? '1 type selected'
                                            : `${numberOfSelectedItems} types selected`
                                    }
                                />
                            )}
                        </InputWrapper>
                    ),
                    [moduleTypes, props.disableFilters, setModuleTypes]
                )}

                {useMemo(
                    () => (
                        <InputWrapper id='module-status-input' labelText='Module status'>
                            {(inputProps) => (
                                <Select
                                    {...inputProps}
                                    dataTestId={FILTER_DATA_IDS.MODULE_STATUS_SELECT}
                                    options={[
                                        ModuleStatus.DRAFT_UNVALIDATED,
                                        ModuleStatus.DRAFT_VALIDATED,
                                        ModuleStatus.REVIEW,
                                        ModuleStatus.REVIEW_MAJOR,
                                        ModuleStatus.REVIEW_MINOR,
                                        ModuleStatus.REVIEW_APPROVED,
                                        ModuleStatus.UAT,
                                        ModuleStatus.TRANSLATION_REQUESTED,
                                        ModuleStatus.MEDIA_TRANSLATION_REQUIRED,
                                        ModuleStatus.TRANSLATION_RECEIVED,
                                        ModuleStatus.PUBLISHED,
                                        ModuleStatus.LIVE,
                                    ]}
                                    isMulti={true}
                                    disabled={!!lockedModuleStatus}
                                    onChange={setModuleStatus}
                                    value={lockedModuleStatus ?? status}
                                    placeholder='Any module statuses'
                                    getTriggerText={getSelectTriggerText}
                                />
                            )}
                        </InputWrapper>
                    ),
                    [lockedModuleStatus, setModuleStatus, status, getSelectTriggerText]
                )}
            </Col>

            {useMemo(
                () =>
                    props.applyFilters && (
                        <Button
                            variant={ButtonVariant.Primary}
                            onClick={props.applyFilters}
                            aria-label='Apply Filters'
                            data-test-id='apply-filters-button'
                        >
                            Apply filters
                        </Button>
                    ),
                [props.applyFilters]
            )}
        </Col>
    )
}

export const ModuleListFilter = React.memo(ModuleListFilterInner)
ModuleListFilter.displayName = 'ModuleListFilter'
